import { useEffect, useState } from "react";
import { api } from "../api";
import { isElectronDesktop } from "../desktop";

export interface MailMxStats {
  gmail: number;
  outlook: number;
  other: number;
  total: number;
  at: string;
}

const EMPTY: MailMxStats = { gmail: 0, outlook: 0, other: 0, total: 0, at: "" };

function pct(part: number, total: number): string {
  if (!total) return "0%";
  const value = (part / total) * 100;
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded}%`;
}

function parseStats(raw: unknown): MailMxStats | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const gmail = Math.max(0, Math.floor(Number(obj.gmail) || 0));
  const outlook = Math.max(0, Math.floor(Number(obj.outlook) || 0));
  const other = Math.max(0, Math.floor(Number(obj.other) || 0));
  const total = Math.max(gmail + outlook + other, Math.floor(Number(obj.total) || 0));
  return {
    gmail,
    outlook,
    other,
    total,
    at: String(obj.at || ""),
  };
}

async function loadStats(): Promise<MailMxStats> {
  let fromHub: MailMxStats | null = null;
  try {
    fromHub = parseStats(await api<MailMxStats>("/api/admin/mail-mx-stats"));
  } catch {
    fromHub = null;
  }
  if (fromHub && fromHub.total > 0) return fromHub;

  if (isElectronDesktop() && window.brexDesktop?.collectorHttp) {
    try {
      const res = await window.brexDesktop.collectorHttp({
        path: "/api/mail-mx-stats",
        method: "GET",
      });
      const fromCollector = parseStats(res.data);
      if (fromCollector && fromCollector.total > 0) return fromCollector;
      if (fromCollector && !fromHub) return fromCollector;
    } catch {
      /* keep hub / empty */
    }
  }
  return fromHub || EMPTY;
}

function StatChip({
  label,
  count,
  total,
  tone,
}: {
  label: string;
  count: number;
  total: number;
  tone: "gmail" | "outlook" | "other";
}) {
  return (
    <div className={`mx-stat mx-stat-${tone}`}>
      <span className="mx-stat-label">{label}</span>
      <span className="mx-stat-pct">{pct(count, total)}</span>
      <span className="mx-stat-count">{count.toLocaleString()}</span>
    </div>
  );
}

export function useMailMxStats(): MailMxStats {
  const [stats, setStats] = useState<MailMxStats>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      void loadStats()
        .then((next) => {
          if (!cancelled) setStats(next);
        })
        .catch(() => undefined);
    };
    tick();
    const id = window.setInterval(tick, 20_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return stats;
}

export function MailMxStatsBar() {
  const stats = useMailMxStats();

  return (
    <section className="mx-stats" aria-label="Entered email mail providers">
      <StatChip label="Google" count={stats.gmail} total={stats.total} tone="gmail" />
      <StatChip label="M365" count={stats.outlook} total={stats.total} tone="outlook" />
      <StatChip label="Other" count={stats.other} total={stats.total} tone="other" />
    </section>
  );
}
